// EmployeeProfileReminderBanner
//
// Shows on the dashboard when an employee has signed up but hasn't yet
// completed their profile (profile_completed_at is null). The
// EmployeeProfileWizard is full-screen blocking on first login, but a
// user can skip it (sessionStorage flag) — without this reminder they
// could forget about it indefinitely.
//
// The banner has two actions:
//   * Complete now — re-opens the wizard (deep-link to /employees/<self>)
//   * Dismiss for now — stores a 24h sessionStorage skip so the banner
//     doesn't follow them around for the rest of the session
//
// Renders nothing when:
//   * employeeRecord is null (firm owner with no employees row)
//   * profile_completed_at is set
//   * the user has dismissed for this session

import React, { useState } from 'react';
import { UserCircle, X, ArrowRight } from 'lucide-react';
import { useRoute } from '../../lib/router';
import ProfileCompletionBar from './ProfileCompletionBar';

const dismissKey = (empId) => `lf_profile_reminder_dismissed_${empId}`;

export default function EmployeeProfileReminderBanner({ employee }) {
  const [route, navigate] = useRoute();
  const [dismissed, setDismissed] = useState(() => {
    try { return !!sessionStorage.getItem(dismissKey(employee?.id || '')); }
    catch { return false; }
  });

  if (!employee || employee.profile_completed_at) return null;
  if (dismissed) return null;

  // Don't double up on the user's own profile page — that page already
  // shows the full ProfileCompletionBar card, which is a richer version
  // of this banner. Showing both is noisy and clicking the banner's CTA
  // would be a no-op (same route → no remount → sessionStorage handoff
  // never fires).
  //
  // useRoute returns { pathname, segments, searchParams } from the hash
  // router — pathname is the leading-slash form like "/employees/<id>".
  const currentPath = route?.pathname || '';
  if (currentPath === `/employees/${employee.id}`) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(dismissKey(employee.id), '1'); }
    catch { /* ignore */ }
    setDismissed(true);
  };

  const handleComplete = () => {
    // One-shot flag the EmployeeDetailPage picks up on mount so the
    // profile edit modal opens immediately on arrival — otherwise the
    // user lands on a read-only view and has to click "Edit Profile"
    // again, which is a confusing two-click handoff.
    try { sessionStorage.setItem(`lf_open_profile_edit_${employee.id}`, '1'); }
    catch { /* ignore */ }
    navigate(`/employees/${employee.id}`);
  };

  const firstName = employee.first_name?.trim() || 'there';

  return (
    <div className="relative rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 p-4 sm:p-5">
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-white/50 dark:hover:bg-slate-800/50 transition"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
          <UserCircle size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">
            Finish setting up your profile, {firstName}
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
            Add a photo, phone, and a short bio so your colleagues know who you are.
            Takes about two minutes.
          </p>

          {/* Inline progress bar — gives a sense of momentum from any
              page, not just the profile page itself. The inline variant
              shows both percent and the bar, so no separate badge. */}
          <div className="mt-3 max-w-sm">
            <ProfileCompletionBar variant="inline" data={employee} />
          </div>

          <button
            onClick={handleComplete}
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition"
          >
            Complete profile <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
